import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import {
  Button,
  ColumnVisibilityState,
  Icon,
  Table,
  TableProps,
  TableVariant,
  formatNumber,
} from '@column/column-ui-kit';

import { defaultACHColumns, useACHTransfersEditTable } from '../_utils/ach';
import { defaultBookColumns, useBookTransfersEditTable } from '../_utils/book';
import { defaultWireColumns, useWireTransfersEditTable } from '../_utils/wire';
import { TransferTemplateForm } from '../types';
import { useDialog } from '~/components/Dialog/Provider';
import {
  AchTransferTemplateResponse,
  BookTransferTemplateResponse,
  TransferTemplateType,
  WireTransferTemplateResponse,
} from '~/repositories/Transfer/TransferTemplateRepository';

interface EditTemplateTableProps {
  type: TransferTemplateType;
}

const TableWrapper = styled.div`
  display: grid;
  gap: 8px;
`;

const AddButton = styled(Button)`
  --button-scale: 1;

  &:after {
    background: ${({ theme }) => theme.gray.blendToBackground(25)};
  }

  &:hover {
    &:after {
      background: ${({ theme }) => theme.gray.blendToBackground(50)};
    }
  }
`;

const TotalAmount = styled.span`
  color: ${({ theme }) => theme.secondary.blendToForeground(400)};
  font-weight: 500;
`;

export const EditTemplateTable: FC<EditTemplateTableProps> = ({ type }) => {
  const { openAlert } = useDialog();
  const { setValue } = useFormContext<TransferTemplateForm>();

  useEffect(() => {
    switch (type) {
      case TransferTemplateType.ACH:
        setValue('visibleColumns', defaultACHColumns);
        break;
      case TransferTemplateType.Wire:
        setValue('visibleColumns', defaultWireColumns);
        break;
      case TransferTemplateType.Book:
        setValue('visibleColumns', defaultBookColumns);
        break;
    }
  }, [type, setValue]);

  const {
    append: achAppend,
    remove: achRemove,
    data: achTransfers,
    columns: achColumns,
    columnVisibility: achColumnVisibility,
  } = useACHTransfersEditTable();

  const {
    append: wireAppend,
    remove: wireRemove,
    data: wireTransfers,
    columns: wireColumns,
    columnVisibility: wireColumnVisibility,
  } = useWireTransfersEditTable();

  const {
    append: bookAppend,
    data: bookTransfers,
    columns: bookColumns,
    columnVisibility: bookColumnVisibility,
    remove: bookRemove,
  } = useBookTransfersEditTable();

  const { total, count } = useMemo(() => {
    const rows =
      type === TransferTemplateType.ACH
        ? achTransfers
        : type === TransferTemplateType.Wire
          ? wireTransfers
          : bookTransfers;
    return {
      total: (rows ?? []).reduce((sum, row) => sum + (row.amount ?? 0), 0),
      count: rows?.length ?? 0,
    };
  }, [type, achTransfers, wireTransfers, bookTransfers]);

  const handleAddClick = useCallback(() => {
    if (type === TransferTemplateType.ACH) {
      achAppend();
    }
    if (type === TransferTemplateType.Wire) {
      wireAppend();
    }
    if (type === TransferTemplateType.Book) {
      bookAppend();
    }
  }, [type, achAppend, wireAppend, bookAppend]);

  const handleDeleteClick = useCallback(
    (id?: string) => {
      if (!id) {
        return;
      }

      openAlert({
        headline: 'Remove transfer?',
        content: 'Are you sure you want to remove this transfer and the related data?',
        confirmButton: {
          label: 'Remove',
          variant: 'danger',
        },
        onSuccess: () => {
          if (type === TransferTemplateType.ACH) {
            achRemove(id);
          } else if (type === TransferTemplateType.Wire) {
            wireRemove(id);
          } else if (type === TransferTemplateType.Book) {
            bookRemove(id);
          }
        },
      });
    },
    [type, achRemove, wireRemove, bookRemove, openAlert]
  );

  const handleColumnVisibilityChange = useCallback(
    (
      visibilityState: ColumnVisibilityState<
        AchTransferTemplateResponse | WireTransferTemplateResponse | BookTransferTemplateResponse
      >
    ) => {
      setValue(
        'visibleColumns',
        Object.entries(visibilityState)
          .filter(([, visible]) => visible)
          .map(([id]) => id)
      );
    },
    [setValue]
  );

  const tableProps: Partial<
    TableProps<AchTransferTemplateResponse | WireTransferTemplateResponse | BookTransferTemplateResponse>
  > = {
    emptyMessage: 'No entries',
    footer: count > 1 ? 'Total' : undefined,
    footerCells: count > 1 ? { amount: <TotalAmount>{formatNumber(total, 'USD')}</TotalAmount> } : undefined,
    getRowId: (row) => row.transferTemplateRowId ?? '',
    rowAction: (row) => (
      <Button type="button" size="small" variant="muted" onClick={() => handleDeleteClick(row.transferTemplateRowId)}>
        <Icon.Trash />
      </Button>
    ),
  };

  return (
    <TableWrapper>
      {type === TransferTemplateType.ACH && (
        <Table<AchTransferTemplateResponse & { isAuthorized?: boolean }>
          columns={achColumns}
          columnVisibility={achColumnVisibility}
          data={achTransfers}
          headerAction={
            <Table.ColumnSelector<AchTransferTemplateResponse & { isAuthorized?: boolean }>
              onChange={handleColumnVisibilityChange}
              value={achColumnVisibility}
              title="Add Parameters"
            />
          }
          variant={TableVariant.Grid}
          {...tableProps}
        />
      )}

      {type === TransferTemplateType.Wire && (
        <Table<WireTransferTemplateResponse & { isAuthorized?: boolean }>
          columns={wireColumns}
          columnVisibility={wireColumnVisibility}
          data={wireTransfers}
          headerAction={
            <Table.ColumnSelector<WireTransferTemplateResponse & { isAuthorized?: boolean }>
              onChange={handleColumnVisibilityChange}
              value={wireColumnVisibility}
              title="Add Parameters"
            />
          }
          variant={TableVariant.Grid}
          {...tableProps}
        />
      )}

      {type === TransferTemplateType.Book && (
        <Table<BookTransferTemplateResponse & { isAuthorized?: boolean }>
          columns={bookColumns}
          columnVisibility={bookColumnVisibility}
          data={bookTransfers}
          headerAction={
            <Table.ColumnSelector<BookTransferTemplateResponse & { isAuthorized?: boolean }>
              onChange={handleColumnVisibilityChange}
              value={bookColumnVisibility}
              title="Add Parameters"
            />
          }
          variant={TableVariant.Grid}
          {...tableProps}
        />
      )}

      <AddButton type="button" size="small" fullWidth variant="muted" onClick={handleAddClick} ariaLabel="Add row">
        <Icon.Plus />
      </AddButton>
    </TableWrapper>
  );
};
